import {
	faChevronLeft,
	faEllipsisVertical,
} from '@fortawesome/pro-regular-svg-icons';
import { Link } from '@tanstack/react-router';
import type { ButtonProps } from '@uturn/ui-kit';
import {
	Button,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
	Icon,
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@uturn/ui-kit';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ParentLocation } from '../types/shipments';
import type { TabType } from './page/tabs';
import { Tabs } from './page/tabs';
import type { PageStickyBarProps } from './page-sticky-bar';
import { PageStickyBar } from './page-sticky-bar';

type ContextAction = {
	label: ReactNode;
	onClick: () => void;
	className?: string;
	renderCondition?: boolean;
	variant?: ButtonProps['variant'];
	override?: ReactNode;
	id?: string;
	disabled?: boolean;
	disabledTooltip?: ReactNode;
};

function ContextActions({
	actions,
	secondaryAction,
}: {
	actions?: ContextAction[];
	secondaryAction?: ContextAction;
}) {
	const { t } = useTranslation();

	if (!actions?.length && !secondaryAction) {
		return null;
	}

	const contextActions =
		actions?.filter(({ renderCondition }) => renderCondition !== false) || [];

	return (
		<div className={!contextActions.length ? 'block md:hidden' : ''}>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button
						variant="outline"
						size="icon"
						id="dropdown-menu-actions-trigger"
					>
						<Icon icon={faEllipsisVertical} />
						<span className="sr-only">{t('TODO', 'Actions')}</span>
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent side="bottom" id="dropdown-menu-actions-content">
					{secondaryAction && secondaryAction.renderCondition !== false && (
						<>
							<DropdownMenuItem
								className="block md:hidden"
								onClick={secondaryAction.onClick}
							>
								{secondaryAction.label}
							</DropdownMenuItem>
							{!!contextActions.length && (
								<DropdownMenuSeparator className="block md:hidden" />
							)}
						</>
					)}
					{contextActions?.map((action, index) => {
						const item = (
							<DropdownMenuItem
								id={action.id}
								className={action.className}
								key={index}
								disabled={action.disabled}
								onClick={action.onClick}
							>
								{action.label}
							</DropdownMenuItem>
						);

						if (action.disabled && action.disabledTooltip) {
							return (
								<TooltipProvider key={index} delayDuration={10}>
									<Tooltip>
										<TooltipTrigger asChild>
											<span className="block">{item}</span>
										</TooltipTrigger>
										<TooltipContent side="left">
											{action.disabledTooltip}
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							);
						}

						return item;
					})}
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
}

export function Page({
	title,
	children,
	backButton,
	actions,
	primaryAction,
	secondaryAction,
	contextActions,
	className,
	stickyBar = undefined,
	tabsStart,
	tabsEnd,
}: {
	title?: string;
	children: ReactNode;
	backButton?: ParentLocation & { label: string };
	actions?: ReactNode;
	contextActions?: ContextAction[];
	primaryAction?: ContextAction;
	secondaryAction?: ContextAction;
	className?: string;
	stickyBar?: PageStickyBarProps;
	tabsStart?: TabType[];
	tabsEnd?: TabType[];
}) {
	return (
		<div
			className={`grid flex-1 space-y-4 py-8 pt-6 @container/content contain-layout ${className}`}
		>
			<div className="flex flex-wrap-reverse items-start justify-between gap-y-2">
				<div className="flex flex-col">
					{backButton && (
						<nav aria-label="Back" className="print:hidden">
							<div className="mb-1 flex items-center spaspace-x-1 text-sm text-muted-foreground">
								<Link
									className="flex items-center gap-1"
									to={backButton.href}
									search={backButton.search}
									params={backButton.params}
								>
									<Icon icon={faChevronLeft} />
									<span>{backButton.label}</span>
								</Link>
							</div>
						</nav>
					)}
					<h2 className="text-3xl font-bold tracking-tight">{title}</h2>
				</div>

				<div className="ml-auto flex space-x-2">
					{actions}
					{secondaryAction &&
						secondaryAction.renderCondition !== false &&
						secondaryAction.override}
					{secondaryAction &&
						secondaryAction.renderCondition !== false &&
						!secondaryAction.override && (
							<Button
								variant={secondaryAction.variant || 'outline'}
								className="hidden md:block"
								onClick={secondaryAction.onClick}
							>
								{secondaryAction.label}
							</Button>
						)}
					{primaryAction &&
						primaryAction.renderCondition !== false &&
						primaryAction.override}
					{primaryAction &&
						!primaryAction.override &&
						primaryAction.renderCondition !== false && (
							<Button
								onClick={primaryAction.onClick}
								variant={primaryAction.variant || 'default'}
							>
								{primaryAction.label}
							</Button>
						)}
					<ContextActions
						actions={contextActions}
						secondaryAction={secondaryAction}
					/>
				</div>
			</div>
			<Tabs start={tabsStart} end={tabsEnd} />
			{children}
			{stickyBar && (
				<PageStickyBar position={stickyBar.position}>
					{stickyBar.children}
				</PageStickyBar>
			)}
		</div>
	);
}
